// AuthShell.tsx — the shell behind Login / ForgotPassword / ResetPassword.
// Modern split layout: a brand hero (headline + feature highlights + compliance) on the left, the
// form column on the right, wrapped in a centred card. The hero collapses on narrow screens so the
// form is full-width. Styles live in AuthShell.css so Vite hot-reloads them reliably.
import type { ReactNode } from 'react';
import { Logo } from '@/foundation/primitives';
import './AuthShell.css';

/** Marketing/onboarding site — shared with WorkspaceRedirect (was duplicated). */
export const ONBOARDING_URL: string =
  import.meta.env.VITE_ONBOARDING_URL ?? 'https://www.mypharmacyiq.com/';

/** Small check icon for the hero feature list (colour comes from the parent). */
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M20 6 L9 17 L4 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const HERO_FEATURES = [
  'Scan-first receiving & dispensing',
  'Append-only, tamper-evident ledger',
  'Controlled-drug register built in',
];

export function AuthShell({
  title,
  deck,
  workspace,
  children,
  footer,
}: {
  /** The page's real h1 — a short sentence ("Welcome back."). */
  title: ReactNode;
  /** Caption line under the h1 (compliance badges live here now). */
  deck?: ReactNode;
  /** Tenant workspace name for the brand row, when resolved. */
  workspace?: ReactNode;
  children: ReactNode;
  /** Bordered footer line (onboarding link, back-to-sign-in). */
  footer?: ReactNode;
}) {
  return (
    <div className="authv2-page">
      <div className="authv2-card">
        {/* Left — brand hero (decorative; hidden on narrow screens) */}
        <aside className="authv2-hero" aria-hidden="true">
          <span className="authv2-orb authv2-orb-a" />
          <span className="authv2-orb authv2-orb-b" />
          <div className="authv2-wordmark">
            Pharmacy<span>IQ</span>
          </div>
          <div className="authv2-hero-body">
            <span className="authv2-eyebrow">Regulated pharmacy operations</span>
            <h2 className="authv2-hero-title">Dispensing you can prove.</h2>
            <p className="authv2-hero-sub">
              Scan-first stock, prescriptions and controlled drugs — every movement recorded on an
              append-only, audit-ready ledger.
            </p>
            <ul className="authv2-features">
              {HERO_FEATURES.map((f) => (
                <li key={f}>
                  <span className="authv2-check">
                    <CheckIcon />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <ul className="authv2-chips">
              <li>MHRA Annex 11</li>
              <li>EU GDP</li>
              <li>GDPR</li>
            </ul>
          </div>
        </aside>

        {/* Right — the form */}
        <main className="authv2-panel">
          <div className="authv2-form">
            <div className="authv2-brandrow">
              <Logo size={22} />
              {workspace && (
                <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                  · {workspace} workspace
                </span>
              )}
            </div>
            <h1 className="authv2-h1">{title}</h1>
            {deck && <p className="caption authv2-deck">{deck}</p>}
            {children}
            {footer && <div className="authv2-footer">{footer}</div>}
          </div>
        </main>
      </div>
    </div>
  );
}

/** Shared error banner for the auth forms. */
export const authErrorBox = {
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--error-soft)',
  color: 'var(--error-dark)',
  fontSize: 'var(--fs-body)',
} as const;

/** Shared inline link style for the auth forms. */
export const authLink = {
  color: 'var(--primary-ink)',
  fontWeight: 600,
  textDecoration: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
} as const;
